import { BoxesApiFactory, V1Box, V1BoxStatus, V1BoxStyle, V1DoorType } from '@vacancorp/enterprise-for-client.vacanservice.com'
import { Box, BoxStatus, BoxStyle, DoorType } from '@/models'
import { api, ApiResponse, ListResponse, createApiError } from './base'
import { HashUtil } from "@/utils/hash";

function convertBoxStatus(rawBoxStatus?: V1BoxStatus): BoxStatus {
  switch (rawBoxStatus) {
    case V1BoxStatus.UNKNOWNBOXSTATUS:
      return BoxStatus.UNKNOWNBOXSTATUS
    case V1BoxStatus.RUNNING:
      return BoxStatus.RUNNING
    case V1BoxStatus.INMAINTENANCE:
      return BoxStatus.IN_MAINTENANCE
    case V1BoxStatus.INPREPARING:
      return BoxStatus.IN_PREPARING
    default:
      return BoxStatus.UNKNOWNBOXSTATUS
  }
}

function convertBoxStyle(rawBoxStyle?: V1BoxStyle): BoxStyle {
  switch (rawBoxStyle) {
    case V1BoxStyle.UNKNOWNBOXSTYLE:
      return BoxStyle.UNKNOWN_BOX_STYLE
    case V1BoxStyle.JAPANESE:
      return BoxStyle.JAPANESE
    case V1BoxStyle.WESTERN:
      return BoxStyle.WESTERN
    default:
      return BoxStyle.UNKNOWN_BOX_STYLE
  }
}

function convertDoorType(rawDoorType?: V1DoorType): DoorType {
  switch (rawDoorType) {
    case V1DoorType.UNKNOWNDOORTYPE:
      return DoorType.UNKNOWN_DOOR_TYPE
    case V1DoorType.NORMALLYOPENED:
      return DoorType.NORMALLY_OPENED
    case V1DoorType.NORMALLYCLOSED:
      return DoorType.NORMALLY_CLOSED
    default:
      return DoorType.UNKNOWN_DOOR_TYPE
  }
}

function createBoxes(rawBoxes?: V1Box[]): Box[] {
  if (!rawBoxes) return [] as Box[]

  const sensorHashUtil = new HashUtil("sensor");

  return rawBoxes.map((b: V1Box): Box => {
    return new Box({
      id: b.id,
      toiletId: b.toiletId,
      name: b.name,
      boxStyle: convertBoxStyle(b.boxStyle),
      vacancyStatus: b.vacancyStatus,
      options: b.options,
      doorType: convertDoorType(b.doorType),
      sensorList: b.sensorList?.map((sensor: { id?: string; databaseId?: number}) => {
        if (sensor.id !== undefined) {
          sensor.databaseId = sensorHashUtil.idFromHash(sensor.id);
        }
        return sensor;
      }),
      sensorMac: b.sensorMac,
      status: convertBoxStatus(b.status),
    })
  })
}

export const NewBoxesApiFunc = () => {
  const webAdapter = BoxesApiFactory(undefined, api.defaults.baseURL, api)

  return {
    listBoxes: async (companyId: string, toiletId: string, filter?: string, page?: number): Promise<ApiResponse<ListResponse<Box>>> => {
      let data, currentPage, totalPage, err

      try {
        const res = await webAdapter.listBoxes(companyId, toiletId, filter, page)
        data = createBoxes(res.data.boxes)
        currentPage = res.data.currentPage
        totalPage = res.data.totalPage
      } catch (e) {
        data = [] as Box[]
        err = createApiError(e)
      }

      return {
        data: {
          list: data,
          currentPage: currentPage || 1,
          totalPage: totalPage || 1
        },
        err,
      }
    },

    getBox: async (companyId: string, toiletId: string, boxId: string, filter?: string): Promise<ApiResponse<Box | undefined>> => {
      let data, err

      try {
        const res = await webAdapter.getBox(companyId, toiletId, boxId, filter)
        data = createBoxes([res.data])[0]
      } catch (e) {
        data = undefined
        err = createApiError(e)
      }

      return {
        data,
        err
      }
    },

    createBox: async (companyId: string, toiletId: string, box: Box): Promise<ApiResponse<Box | undefined>> => {
      let data, err

      try {
        const res = await webAdapter.createBox(companyId, toiletId, { data: box })
        data = createBoxes([res.data])[0]
      } catch (e) {
        data = undefined
        err = createApiError(e)
      }

      return {
        data,
        err
      }
    },

    updateBox: async (companyId: string, toiletId: string, boxId: string, box: Box): Promise<ApiResponse<Box | undefined>> => {
      let data, err

      try {
        const res = await webAdapter.updateBox(companyId, toiletId, boxId, { data: box })
        data = createBoxes([res.data])[0]
      } catch (e) {
        data = undefined
        err = createApiError(e)
      }

      return {
        data,
        err
      }
    },

    deleteBox: async (companyId: string, toiletId: string, boxId: string): Promise<ApiResponse<Box | undefined>> => {
      let data, err

      try {
        await webAdapter.deleteBox(companyId, toiletId, boxId)
      } catch (e) {
        err = createApiError(e)
      }

      return {
        data,
        err
      }
    }
  }
}